<template>
  <!-- Not a PayMe business — invite to join -->
  <div v-if="!hasPayMe" class="flex flex-col items-center justify-center w-full max-w-md mx-auto py-16 px-6 text-center gap-3">
    <div class="w-14 h-14 rounded-2xl flex items-center justify-center" style="background:#dcfce7">
      <i class="pi pi-building-columns text-2xl" style="color:#15803d"/>
    </div>
    <div class="text-[15px] font-bold text-gray-900">העברות לבנק זמינות עם TimeIt Pay</div>
    <div class="text-[12.5px] text-gray-500 leading-relaxed">
      לקוחות TimeIt Pay רואים כאן בדיוק מתי הכסף שנסלק מגיע לחשבון הבנק — העברות שבוצעו וסכומים שבדרך.
      רוצים להצטרף? דברו איתנו ונחבר אתכם.
    </div>
    <button
      type="button"
      class="mt-2 flex items-center gap-2 bg-primary text-white text-[13px] font-semibold rounded-full px-5 py-2.5"
      @click="contactUs"
    >
      <i class="pi pi-whatsapp"/>
      דברו איתנו
    </button>
  </div>

  <div v-else class="flex flex-col w-full max-w-2xl">
    <!-- loading -->
    <div v-if="isLoading" class="flex flex-col gap-2.5">
      <Skeleton height="7rem" borderRadius="1.25rem"/>
      <Skeleton v-for="n of 3" :key="n" height="4.5rem" borderRadius="1.1rem"/>
    </div>
    <retry-loader v-else-if="isErrorState" :isErrorState="isErrorState" @retry-function="fetchTransfers"/>

    <template v-else>
      <!-- upcoming transfers -->
      <div
        v-if="nextTransfer"
        class="rounded-[20px] px-4 py-4 text-white"
        style="background: linear-gradient(140deg,#065f46 0%,#15803d 80%); box-shadow: 0 6px 18px rgba(21,128,61,.2)">
        <div class="flex items-center gap-2 text-xs opacity-85">
          <span class="w-[7px] h-[7px] rounded-full pulse-dot" style="background:#6ee7b7"/>
          בדרך לחשבון שלך<template v-if="nextTransfer.date"> · {{ formatDate(nextTransfer.date) }}</template>
        </div>
        <div class="text-[28px] font-bold leading-tight mt-1" style="letter-spacing: -0.5px">
          {{ formatCurrency(totalFuture) }}
        </div>
        <div class="text-[11px] opacity-80 mt-1">נסלק וממתין להעברה לחשבון הבנק</div>
      </div>

      <!-- completed transfers -->
      <h3 class="section-title">הועברו לחשבון שלך</h3>
      <div v-if="!data?.completed.length" class="flex flex-col items-center py-10 text-gray-400 gap-2">
        <i class="pi pi-inbox text-3xl"/>
        <span class="text-sm">עדיין לא בוצעו העברות לחשבון</span>
      </div>
      <div v-else class="flex flex-col gap-2.5">
        <div
          v-for="transfer of data.completed"
          :key="transfer.id"
          class="bg-white border border-gray-200 rounded-[18px] overflow-hidden">
          <div
            v-wave
            class="flex items-center gap-3 px-4 py-3.5 cursor-pointer"
            @click="toggleTransfer(transfer)">
            <div class="w-9 h-9 rounded-[11px] shrink-0 flex items-center justify-center" style="background:#dcfce7">
              <i class="pi pi-check text-sm" style="color:#15803d"/>
            </div>
            <div class="flex-1 min-w-0">
              <div class="text-[13.5px] font-bold">{{ formatCurrency(transfer.amount) }}</div>
              <div class="text-[11px] text-gray-500 mt-0.5">
                הועבר ב-{{ formatDate(transfer.date) }}<template v-if="transfer.coveredMonth"> · עבור {{ monthLabel(transfer.coveredMonth) }}</template>
              </div>
            </div>
            <span class="text-[10px] font-bold rounded-full px-2 py-0.5 shrink-0" style="background:#dcfce7;color:#15803d">הועבר</span>
            <i
              class="pi pi-chevron-down text-sm text-gray-400 transition-transform duration-200"
              :style="{transform: openTransferId === transfer.id ? 'rotate(180deg)' : 'none'}"/>
          </div>

          <!-- the transactions this withdrawal paid out (lazy-fetched) -->
          <div v-if="openTransferId === transfer.id" class="border-t border-gray-100 px-4 pb-3 bg-gray-50">
            <div v-if="txState[transfer.id]?.loading" class="flex flex-col gap-2 py-3">
              <Skeleton v-for="n of 3" :key="n" height="2.8rem" borderRadius="0.75rem"/>
            </div>
            <div v-else-if="txState[transfer.id]?.error" class="flex items-center gap-2 py-3 text-xs text-gray-500">
              <i class="pi pi-exclamation-circle text-gray-400"/>
              לא הצלחנו לטעון את העסקאות.
              <span class="font-bold text-primary cursor-pointer" @click="fetchTransferTransactions(transfer)">נסו שוב</span>
            </div>
            <template v-else-if="txState[transfer.id]?.rows">
              <div class="text-[11px] text-gray-500 pt-2.5 pb-0.5">
                עסקאות {{ monthLabel(txState[transfer.id].month) }} שנכללו בהעברה:
              </div>
              <div v-if="!txState[transfer.id].rows!.length" class="text-xs text-gray-400 py-4 text-center">
                לא נמצאו עסקאות לחודש זה
              </div>
              <div
                v-for="(t, i) of txState[transfer.id].rows"
                :key="t.uuid"
                v-wave
                class="flex items-center gap-3 py-2.5"
                :class="[
                  {'border-b border-gray-100': i < txState[transfer.id].rows!.length - 1},
                  t._id ? 'cursor-pointer' : '',
                ]"
                @click="t._id && openRow(t)">
                <div class="w-8 h-8 rounded-full bg-gray-200 text-gray-600 shrink-0 flex items-center justify-center text-[11.5px] font-bold">
                  {{ (t.customerName || 'לקוח').slice(0, 2) }}
                </div>
                <div class="flex-1 min-w-0">
                  <div class="text-[12.5px] font-semibold truncate">{{ t.customerName }}</div>
                  <div class="text-[10.5px] text-gray-500 truncate mt-0.5">{{ t.description }}</div>
                </div>
                <div class="text-left shrink-0">
                  <div class="text-[13px] font-bold">{{ formatCurrency(t.paidPrice ?? t.price) }}</div>
                  <div class="text-[10px] text-gray-400 mt-0.5" dir="ltr">{{ formatWhen(t) }}</div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div class="text-[11px] text-gray-400 text-center mt-4 leading-relaxed">
        העברה שמבוצעת בחודש מסוים משלמת את העסקאות שנסלקו בחודש שלפניו
      </div>
    </template>
  </div>
</template>

<script lang="ts" setup>
import {computed, onMounted, reactive, ref} from 'vue';
import {DateTime} from 'luxon';
import Skeleton from 'primevue/skeleton';
import {
  IPaymeBankTransfer,
  IPaymeBankTransfersResponse,
  IPaymentTransactionRow,
  IPaymentsResponse,
} from '@torim-app/types';
import {useDrawer} from '@torim-app/shared-components';
import {sendWhatsApp} from '@torim-app/utils';

import PaymentDetailsDrawer from '@app/components/admin/pages/customer/PaymentDetailsDrawer.vue';
import {PaymentsApi} from '@app/api/admin';
import {formatCurrency} from '@app/components/admin/analytics/analytics-charts';
import {trackEvent} from '@app/composables/posthog';

const props = defineProps<{ hasPayMe: boolean }>();

const {openDrawer} = useDrawer();

const data = ref<IPaymeBankTransfersResponse | null>(null);
const isLoading = ref(true);
const isErrorState = ref(false);
const openTransferId = ref<string | null>(null);

interface ITransferTxState {
  loading: boolean;
  error: boolean;
  month?: string;
  rows?: IPaymentTransactionRow[];
}
const txState = reactive<Record<string, ITransferTxState>>({});

const totalFuture = computed(() =>
  (data.value?.future ?? []).reduce((s, f) => s + f.amount, 0));
const nextTransfer = computed(() => data.value?.future[0] ?? null);

const fetchTransfers = async () => {
  try {
    isErrorState.value = false;
    isLoading.value = true;
    data.value = await PaymentsApi.getBankTransfers();
  } catch (e) {
    console.error(e);
    isErrorState.value = true;
  } finally {
    isLoading.value = false;
  }
};

const fetchTransferTransactions = async (transfer: IPaymeBankTransfer) => {
  txState[transfer.id] = {loading: true, error: false};
  try {
    const res = await PaymentsApi.getBankTransferTransactions(transfer.date);
    txState[transfer.id] = {loading: false, error: false, month: res.month, rows: res.transactions};
  } catch (e) {
    console.error(e);
    txState[transfer.id] = {loading: false, error: true};
  }
};

const toggleTransfer = (transfer: IPaymeBankTransfer) => {
  if (openTransferId.value === transfer.id) {
    openTransferId.value = null;
    return;
  }
  trackEvent('payments_bank_transfer_opened');
  openTransferId.value = transfer.id;
  if (!txState[transfer.id]?.rows && !txState[transfer.id]?.loading) {
    fetchTransferTransactions(transfer);
  }
};

const formatDate = (iso: string) => {
  const dt = DateTime.fromISO(iso).setZone('Asia/Jerusalem').setLocale('he');
  return dt.isValid ? dt.toFormat('d.M.yy') : '';
};

const monthLabel = (month?: string) => {
  if (!month) return '';
  const dt = DateTime.fromFormat(month, 'yyyy-MM').setLocale('he');
  return dt.isValid ? dt.toFormat('MMMM yyyy') : month;
};

const formatWhen = (t: IPaymentTransactionRow): string => {
  const dt = DateTime.fromISO(t.date).setZone('Asia/Jerusalem');
  if (!dt.isValid) return '';
  return `${dt.toFormat('d.M')} · ${t.hour ?? dt.toFormat('HH:mm')}`;
};

const contactUs = () => {
  trackEvent('bank_transfers_join_payme_clicked');
  sendWhatsApp('972559299380', 'היי, אשמח לשמוע פרטים על הצטרפות ל-TimeIt Pay.');
};

// rows joined to a local payment request open the details drawer
const openRow = (row: IPaymentTransactionRow) => {
  trackEvent('payments_transaction_row_opened', {status: row.status});
  const selectedPayment = {
    _id: row._id,
    uuid: row.uuid,
    paymentReason: row.paymentReason,
    chargedDate: row.chargedDate,
    paidPrice: row.paidPrice,
    status: row.status,
    ezCountInvoiceLink: row.ezCountInvoiceLink,
    paymentMethod: row.paymentMethod,
    transactionNumber: row.transactionNumber,
    price: row.price,
    createdAt: row.createdAt,
    refundedAmount: row.refundedAmount,
    refunds: row.refunds,
  } as unknown as IPaymentsResponse;

  openDrawer(PaymentDetailsDrawer, {selectedPayment}, {title: 'פרטי תשלום'});
};

onMounted(() => {
  trackEvent('payments_bank_transfers_viewed');
  if (props.hasPayMe) fetchTransfers();
});
</script>

<style scoped lang="scss">
.section-title {
  font-size: 13.5px;
  font-weight: 700;
  color: rgb(24 24 27);
  margin: 18px 3px 9px;
}

.pulse-dot {
  animation: payout-pulse 1.6s ease-in-out infinite;
}

@keyframes payout-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}
</style>
